import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrialContext = createContext();

export const useTrialContext = () => {
  const context = useContext(TrialContext);
  if (!context) {
    throw new Error('useTrialContext must be used within a TrialProvider');
  }
  return context;
};

export const TrialProvider = ({ children }) => {
  const [trialEmail, setTrialEmail] = useState(() => localStorage.getItem('trialEmail') || '');
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check trial status on mount and when email changes
  useEffect(() => {
    if (trialEmail) {
      checkTrialStatus(trialEmail);
    }
  }, [trialEmail]);

  const startTrial = async (email) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/trial/start`, { email });
      if (response.data.success) {
        localStorage.setItem('trialEmail', email);
        setTrialEmail(email);
        await checkTrialStatus(email);
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || 'Failed to start trial';
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const checkTrialStatus = async (email) => {
    if (!email) return null;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/trial/status/${encodeURIComponent(email)}`);
      setTrialStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking trial status:', error);
      setTrialStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const recordQuizGeneration = async () => {
    if (!trialEmail) return { success: false, error: 'No trial email' };
    
    try {
      const response = await axios.post(`${API_URL}/api/trial/record-quiz/${encodeURIComponent(trialEmail)}`);
      // Refresh status after recording
      await checkTrialStatus(trialEmail);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to record quiz';
      return { success: false, error: message };
    }
  };

  const canGenerateQuiz = () => {
    if (!trialStatus) return false;
    if (trialStatus.is_subscribed) return true;
    if (!trialStatus.is_active) return false;
    return trialStatus.can_generate !== false;
  };

  const clearTrial = () => {
    localStorage.removeItem('trialEmail');
    setTrialEmail('');
    setTrialStatus(null);
  };

  const value = {
    trialEmail,
    trialStatus,
    loading,
    startTrial,
    checkTrialStatus,
    recordQuizGeneration,
    canGenerateQuiz,
    clearTrial,
    isTrialActive: trialStatus?.is_active || false,
    isSubscribed: trialStatus?.is_subscribed || false,
    daysRemaining: trialStatus?.days_remaining || 0,
    quizzesToday: trialStatus?.quizzes_today || 0,
    quizzesLimit: trialStatus?.quizzes_limit || 3
  };

  return (
    <TrialContext.Provider value={value}>
      {children}
    </TrialContext.Provider>
  );
};

export default TrialContext;
